import { Outlet, Navigate, Link, useNavigate, useLocation } from 'react-router-dom';
import { getToken, getAdminUsername, setToken, setAdminUsername } from '../../lib/api';
import { Button } from '../../components/ui/button';
import {
  LogOut,
  ExternalLink,
  Settings as SettingsIcon,
  Map,
  ShoppingBag,
  Users,
  GraduationCap,
  Ticket,
  BadgePercent,
} from 'lucide-react';

export default function AdminLayout() {
  const navigate = useNavigate();
  const loc = useLocation();
  const token = getToken();
  if (!token) return <Navigate to="/admin/login" replace />;

  const tabs = [
    { to: '/admin', label: 'Settings', icon: SettingsIcon, exact: true },
    { to: '/admin/bookings', label: 'Bookings', icon: Ticket },
    { to: '/admin/trips', label: 'Trips', icon: Map },
    { to: '/admin/shop', label: 'Shop', icon: ShoppingBag },
    { to: '/admin/promos', label: 'Promos', icon: BadgePercent },
    { to: '/admin/team', label: 'Team', icon: Users },
    { to: '/admin/courses', label: 'Courses', icon: GraduationCap },
  ];

  const logout = () => {
    setToken(null);
    setAdminUsername(null);
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#0a1628] text-white">
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#07101f]/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/admin" className="flex items-center gap-3">
            <img src="/brand/logo.jpeg" alt="" className="h-9 w-9 rounded-full object-cover ring-1 ring-[#c8e600]/40" />
            <div className="flex flex-col leading-none">
              <span className="font-display text-sm font-semibold">Arab Diver Tours</span>
              <span className="text-[10px] uppercase tracking-[0.18em] text-white/55">Admin Panel</span>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" target="_blank" rel="noopener noreferrer" className="text-xs text-white/65 hover:text-white inline-flex items-center gap-1">
              View Site <ExternalLink className="h-3 w-3" />
            </Link>
            <div className="hidden sm:block text-xs text-white/55">{getAdminUsername()}</div>
            <Button onClick={logout} variant="ghost" size="sm" data-testid="admin-logout-button" className="text-white/80 hover:text-white hover:bg-white/8">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex gap-1 overflow-x-auto scrollbar-thin pb-1 mb-6" data-testid="admin-dashboard-tabs">
          {tabs.map((t) => {
            const active = t.exact ? loc.pathname === t.to : loc.pathname.startsWith(t.to);
            const Icon = t.icon;
            return (
              <Link
                key={t.to}
                to={t.to}
                data-testid={`admin-tab-${t.label.toLowerCase()}`}
                className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm transition-colors whitespace-nowrap ${
                  active
                    ? 'bg-[#00b4d8]/15 text-[#00b4d8] border border-[#00b4d8]/40'
                    : 'text-white/65 hover:text-white hover:bg-white/5 border border-transparent'
                }`}
              >
                <Icon className="h-4 w-4" /> {t.label}
              </Link>
            );
          })}
        </div>
        <Outlet />
      </div>
    </div>
  );
}
